/**
 * ---------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * ---------------------------------------------------------------------------------------------
 */

import { EntityState, createSelector, PayloadAction, createEntityAdapter, createSlice, lruMemoize } from "@reduxjs/toolkit";
import { WritableDraft } from "immer";
import { isEqual } from "lodash-es";

import {
  Period,

} from "@tdp/modules/apps";

import { Metric } from '../abstractions';
import {
  convertToActiveUserTrendData,
  convertToAreaCountryData,
  convertToCapabilityMultiLineData,
  convertToCapabilityTrendData,
  convertToHostBarChartData,
  convertToHostMultiLineData,
  convertToHostTrendData,
  convertToOSBarChartData,
  convertToOSTrendData,
  convertToPlatformBarChartData,
  convertToUsageMultiLineData,
  convertToUsageSingleLineData,
  extractUsageByIntensity
} from "./AnalyticsV2ReducerUtils";
import {
  IAnalyticsV2APIResponse,
  IAnalyticsV2APIResponseMetric,
  IAnalyticsV2PreconditionFailedError
} from '../abstractions/IAnalyticsAPI';
import { GroupName } from "../abstractions/IAnalyticsFilters";


export interface IAnalyticsV2MetricData extends IAnalyticsV2APIResponseMetric {
  groupName: GroupName;
}

const appAnalyticsMetricDataEntityAdapter = createEntityAdapter<IAnalyticsV2MetricData, string>({
  selectId: (metric) => generateMetricId(metric),
});

const reduceApiSuccess = (
  state: WritableDraft<EntityState<IAnalyticsV2MetricData, string>>,
  apiResponse: IAnalyticsV2APIResponse
) => {
  const analyticsMetricData: IAnalyticsV2MetricData[] = apiResponse.successes.flatMap(res => res.metrics.map(metric => ({ ...metric, groupName: res.groupName })));
  appAnalyticsMetricDataEntityAdapter.upsertMany(state, analyticsMetricData);
}

const initialState = appAnalyticsMetricDataEntityAdapter.getInitialState({
  preconditionError: null as IAnalyticsV2PreconditionFailedError | null,
});
const analyticsMetriDataSelector = appAnalyticsMetricDataEntityAdapter.getSelectors();

// metric data selectors
export const getMetricDataByGroupName = (groupName: GroupName) => createSelector(
  [analyticsMetriDataSelector.selectAll],
  (metrics) => metrics.filter(m => m.groupName === groupName),
  {
    memoize: lruMemoize,
    memoizeOptions: {
      equalityCheck: isEqual
    }
  }
);

export const getMetricDataByGroupNameAndMetric = (groupName: GroupName, metric: Metric) => createSelector(
  [getMetricDataByGroupName(groupName)],
  (metrics) => metrics.filter(m => m.metric === metric),
  {
    memoize: lruMemoize,
    memoizeOptions: {
      equalityCheck: isEqual
    }
  }
);

export const getMetricDataByGroupNameAndMetricAndPeriod = (groupName: GroupName, metric: Metric, period: Period) => createSelector(
  [getMetricDataByGroupNameAndMetric(groupName, metric)],
  (metrics) => metrics.filter(m => m.period === period),
  {
    memoize: lruMemoize,
    memoizeOptions: {
      equalityCheck: isEqual
    }
  }
);


// widget data selectors
export const getRetentionIntensityWidgetData = createSelector(
  [getMetricDataByGroupName(GroupName.UsageData)],
  (metrics) => ({
    retention: convertToUsageMultiLineData(metrics),
    intensity: extractUsageByIntensity(metrics)
  })
);

export const getUsageByTimePeriodWidgetData = createSelector(
  [getMetricDataByGroupNameAndMetric(GroupName.UsageData, Metric.Active)],
  (metrics) => convertToUsageSingleLineData(metrics)
);

export const getUsageSummaryWidgetData = createSelector(
  [getMetricDataByGroupNameAndMetric(GroupName.UsageData, Metric.Active)],
  (metrics) => convertToActiveUserTrendData(metrics)
);

export const getRegionWidgetData = createSelector(
  [getMetricDataByGroupName(GroupName.Area)],
  (metrics) => convertToAreaCountryData(metrics)
);

export const getHostCapabilityWidgetData = createSelector(
  [getMetricDataByGroupName(GroupName.HostCapability)],
  (metrics) => ({
    usage: convertToCapabilityMultiLineData(metrics),
    trend: convertToCapabilityTrendData(metrics)
  })
);

export const getHostUsageWidgetData = createSelector(
  [getMetricDataByGroupName(GroupName.Host)],
  (metrics) => ({
    trend: convertToHostTrendData(metrics),
    bar: convertToHostBarChartData(metrics),
    multiLine: convertToHostMultiLineData(metrics)
  })
);

export const getOSUsageWidgetData = createSelector(
  [getMetricDataByGroupName(GroupName.OS)],
  (metrics) => ({
    trend: convertToOSTrendData(metrics),
    bar: convertToOSBarChartData(metrics)
  })
);

export const getPlatformUsageWidgetData = createSelector(
  [getMetricDataByGroupName(GroupName.Platform)],
  (metrics) => convertToPlatformBarChartData(metrics)
);

export const analyticsV2MetricsSlice = createSlice({
  name: 'analyticsV2MetricData',
  initialState,
  reducers: {
    analyticsAPISuccess(state, action: PayloadAction<IAnalyticsV2APIResponse>) {
      reduceApiSuccess(state, action.payload);
    },
    resetMetricsData(state) {
      appAnalyticsMetricDataEntityAdapter.removeAll(state);
      state.preconditionError = null;
    },
    setPreconditionErrorAndClearData(
      state,
      action: PayloadAction<IAnalyticsV2PreconditionFailedError>
    ) {
      // Populate the preconditionError
      state.preconditionError = action.payload;

      // Remove all entities from the adapter
      appAnalyticsMetricDataEntityAdapter.removeAll(state);
    },
    removeMetricsByGroupName(state, action: PayloadAction<GroupName>) {
      const groupNameToRemove = action.payload;
      const idsToRemove = analyticsMetriDataSelector
        .selectAll(state)
        .filter((metric) => metric.groupName === groupNameToRemove)
        .map((metric) => generateMetricId(metric));
      appAnalyticsMetricDataEntityAdapter.removeMany(state, idsToRemove);
    },
    removeMetricsByGroupNameAndMetricTypes(
      state,
      action: PayloadAction<{ groupName: GroupName; metricTypes: Metric[] }>
    ) {
      const { groupName, metricTypes } = action.payload;
      const idsToRemove = analyticsMetriDataSelector
        .selectAll(state)
        .filter(
          (metric) =>
            metric.groupName === groupName &&
            metricTypes.includes(metric.metric)
        )
        .map((metric) => generateMetricId(metric));
      appAnalyticsMetricDataEntityAdapter.removeMany(state, idsToRemove);
    },
  },
  selectors: {
    getMetricDataById: (s, id: string) => analyticsMetriDataSelector.selectById(s, id),
    getMetricDataByGroupName: (s, groupName: GroupName) => analyticsMetriDataSelector.selectAll(s).filter(m => m.groupName === groupName),
    getMetricDataByGroupNameAndMetric: (s, groupName: GroupName, metric: Metric) => analyticsMetriDataSelector.selectAll(s).filter(m => m.groupName === groupName && m.metric === metric),
    getPreconditionError: (s) => s.preconditionError, // Selector for preconditionError
    getUsageSummaryWidgetData,
    getUsageByTimePeriodWidgetData,
    getRetentionIntensityWidgetData,
    getRegionWidgetData,
    getHostCapabilityWidgetData,
    getHostUsageWidgetData,
    getOSUsageWidgetData,
    getPlatformUsageWidgetData
  },
});

function generateMetricId(metric: IAnalyticsV2MetricData): string {
  return `${metric.groupName}-${metric.metric}-${metric.os ?? ""}-${metric.platform ?? ""}-${metric.region ?? ""}-${metric.capability ?? ""}-${metric.host ?? ""}-${metric.period}`;
}
